/**
 * lib/useTreatments.ts — the live treatment menu.
 * Reads a single Firestore document `treatments/menu` (edited from the
 * portal's Treatments page). Falls back to the static seed in
 * data/treatments.ts when the doc is missing or unreachable, so the public
 * site always renders.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import {
  TREATMENT_MENU,
  WINTER_SPECIALS,
  type Treatment,
  type TreatmentCategory,
} from '@/data/treatments';

export interface MenuData {
  categories: TreatmentCategory[];
  winterSpecials: Treatment[];
}

export const SEED_MENU: MenuData = {
  categories: TREATMENT_MENU,
  winterSpecials: WINTER_SPECIALS,
};

export function useTreatments() {
  const [menu, setMenu] = useState<MenuData>(SEED_MENU);
  const [live, setLive] = useState(false);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'treatments', 'menu'),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data() as Partial<MenuData>;
          setMenu({
            categories: d.categories?.length ? d.categories : SEED_MENU.categories,
            winterSpecials: d.winterSpecials ?? SEED_MENU.winterSpecials,
          });
          setLive(true);
        } else {
          setMenu(SEED_MENU);
          setLive(false);
        }
      },
      () => {
        setMenu(SEED_MENU);
        setLive(false);
      },
    );
  }, []);

  const all: Treatment[] = [
    ...menu.categories.flatMap((c) => c.treatments),
    ...menu.winterSpecials,
  ];

  return { categories: menu.categories, winterSpecials: menu.winterSpecials, all, live };
}
